import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { usePlanGating } from '@/hooks/usePlanGating';
import {
  listCollaborators,
  inviteCollaborator,
  updateCollaboratorRole,
  removeCollaborator,
} from '@/api/collaborators';
import type { Collaborator } from '@/api/collaborators';

interface CollaboratorPanelProps {
  mapId: string;
  onOpenPricing?: () => void;
}

export function CollaboratorPanel({ mapId, onOpenPricing }: CollaboratorPanelProps) {
  const { t } = useTranslation();
  const currentUser = useAuthStore((s) => s.user);
  const { canCollaborate } = usePlanGating();

  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'editor' | 'viewer'>('editor');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const fetchCollaborators = useCallback(async () => {
    if (!canCollaborate) return;
    setLoadingList(true);
    setListError(null);
    try {
      const list = await listCollaborators(mapId);
      setCollaborators(list);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load collaborators';
      setListError(message);
    } finally {
      setLoadingList(false);
    }
  }, [mapId, canCollaborate]);

  useEffect(() => {
    void fetchCollaborators();
  }, [fetchCollaborators]);

  const handleInvite = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setInviting(true);
    setInviteError(null);
    setInviteSuccess(null);
    try {
      const resp = await inviteCollaborator(mapId, trimmed, inviteRole);
      setInviteSuccess(t('collaborators.invited', { email: resp.email, role: resp.role }));
      setEmail('');
      void fetchCollaborators();
    } catch (err: unknown) {
      if (err instanceof Error && err.message.includes('404')) {
        setInviteError(t('collaborators.mustHaveAccount'));
      } else {
        const message = err instanceof Error ? err.message : 'Failed to invite collaborator';
        setInviteError(message);
      }
    } finally {
      setInviting(false);
    }
  }, [mapId, email, inviteRole, fetchCollaborators, t]);

  const handleRoleChange = useCallback(async (userId: string, role: 'editor' | 'viewer') => {
    try {
      await updateCollaboratorRole(mapId, userId, role);
      setCollaborators((prev) =>
        prev.map((c) => (c.userId === userId ? { ...c, role } : c)),
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update role';
      setListError(message);
    }
  }, [mapId]);

  const handleRemove = useCallback(async (userId: string) => {
    try {
      await removeCollaborator(mapId, userId);
      setCollaborators((prev) => prev.filter((c) => c.userId !== userId));
      setConfirmRemove(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove collaborator';
      setListError(message);
    }
  }, [mapId]);

  // Plan gating: show upgrade prompt for free/hobby plans
  if (!canCollaborate) {
    return (
      <div style={{ padding: '16px 0 0' }}>
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--bg-info)',
            borderRadius: 6,
            border: '1px solid var(--bg-info)',
          }}
        >
          <p style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm)', color: 'var(--text-primary)' }}>
            {t('collaborators.upgradeToInvite')}
          </p>
          {onOpenPricing && (
            <button
              onClick={onOpenPricing}
              style={{
                display: 'inline-block',
                padding: '6px 14px',
                background: 'var(--color-secondary)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              {t('common.upgrade')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 0 0' }}>
      {/* Invite section */}
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 6 }}>
        {t('collaborators.inviteByEmail')}
      </label>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          type="email"
          placeholder={t('collaborators.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleInvite();
          }}
          style={{
            flex: 1,
            padding: '8px 12px',
            background: 'var(--bg-app)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-default)',
            borderRadius: 6,
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family)',
            outline: 'none',
          }}
        />
        <select
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value as 'editor' | 'viewer')}
          style={{
            padding: '8px 8px',
            background: 'var(--bg-app)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-default)',
            borderRadius: 6,
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-family)',
            outline: 'none',
          }}
        >
          <option value="editor">{t('collaborators.editor')}</option>
          <option value="viewer">{t('collaborators.viewer')}</option>
        </select>
        <button
          onClick={() => void handleInvite()}
          disabled={inviting || !email.trim()}
          style={{
            padding: '8px 14px',
            background: inviting || !email.trim() ? 'var(--border-strong)' : 'var(--color-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 'var(--font-size-sm)',
            fontWeight: 500,
            cursor: inviting || !email.trim() ? 'default' : 'pointer',
            fontFamily: 'var(--font-family)',
            whiteSpace: 'nowrap',
          }}
        >
          {inviting ? t('collaborators.inviting') : t('collaborators.invite')}
        </button>
      </div>

      {inviteError && (
        <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--color-danger)' }}>
          {inviteError}
        </p>
      )}
      {inviteSuccess && (
        <p style={{ margin: '0 0 8px', fontSize: 12, color: '#22c55e' }}>
          {inviteSuccess}
        </p>
      )}

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--bg-surface-raised)', margin: '12px 0' }} />

      {/* Collaborator list */}
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 8 }}>
        {t('collaborators.collaborators')}
      </label>

      {loadingList && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>{t('common.loading')}</p>
      )}
      {listError && (
        <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--color-danger)' }}>{listError}</p>
      )}

      {!loadingList && collaborators.length === 0 && (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>
          {t('collaborators.noCollaborators')}
        </p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {collaborators.map((collab) => (
          <div
            key={collab.userId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 8px',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-app)',
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {collab.email}
              {currentUser && collab.userId === currentUser.userId && (
                <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--font-size-xs)', marginLeft: 6 }}>{t('collaborators.you')}</span>
              )}
            </span>

            {confirmRemove === collab.userId ? (
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>{t('collaborators.removeConfirm')}</span>
                <button
                  onClick={() => void handleRemove(collab.userId)}
                  style={{
                    background: 'var(--bg-error)',
                    color: 'var(--text-error)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    padding: '2px 8px',
                    fontSize: 'var(--font-size-xs)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {t('collaborators.yes')}
                </button>
                <button
                  onClick={() => setConfirmRemove(null)}
                  style={{
                    background: 'var(--bg-surface-raised)',
                    color: 'var(--text-secondary)',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    padding: '2px 8px',
                    fontSize: 'var(--font-size-xs)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {t('collaborators.no')}
                </button>
              </div>
            ) : (
              <>
                <select
                  value={collab.role}
                  onChange={(e) =>
                    void handleRoleChange(collab.userId, e.target.value as 'editor' | 'viewer')
                  }
                  style={{
                    padding: '2px 6px',
                    background: 'var(--bg-surface)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-xs)',
                    fontFamily: 'var(--font-family)',
                    outline: 'none',
                  }}
                >
                  <option value="editor">{t('collaborators.editor')}</option>
                  <option value="viewer">{t('collaborators.viewer')}</option>
                </select>
                <button
                  onClick={() => setConfirmRemove(collab.userId)}
                  title="Remove collaborator"
                  style={{
                    background: 'transparent',
                    color: 'var(--text-tertiary)',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-base)',
                    padding: '2px 6px',
                    borderRadius: 'var(--radius-sm)',
                    lineHeight: 1,
                  }}
                >
                  x
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
